import { useEffect, useState } from "react";
import React from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, NBTextarea, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, Calendar as CalIcon, ChevronLeft, ChevronRight, Link2, RefreshCw, Unlink } from "lucide-react";

const COLORS = ["#E8B96B", "#B8CFA9", "#E9B5A0", "#A8BFD1"];

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(new Date());
  const [view, setView] = useState("month"); // 'month' | 'week' | 'day'
  const [googleStatus, setGoogleStatus] = useState({ connected: false });
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", start: "", end: "",
    color: "#E8B96B", member_ids: [], location: ""
  });

  const load = async () => {
    const [e, m, g] = await Promise.all([api.get("/api/events"), api.get("/api/members"), api.get("/api/google/status")]);
    setEvents(e.data);
    setMembers(m.data);
    setGoogleStatus(g.data);
  };
  useEffect(() => { load(); }, []);

  // Handle ?google=connected redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "connected") {
      toast.success("Google Calendar connected!");
      window.history.replaceState({}, "", "/calendar");
      load();
    } else if (params.get("google") === "error") {
      toast.error("Google Calendar connection failed");
      window.history.replaceState({}, "", "/calendar");
    }
  }, []);

  const connectGoogle = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/api/google/oauth/start");
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
      setConnecting(false);
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    await api.post("/api/google/oauth/disconnect");
    toast.success("Disconnected");
    load();
  };

  const syncGoogle = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/api/google/sync-import");
      toast.success(`Imported ${data.imported} new event${data.imported !== 1 ? "s" : ""} from Google`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    }
    setSyncing(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/api/events", form);
      if (data.google_id) {
        toast.success("Event added — synced to Google Calendar");
      } else {
        toast.success("Event added!");
      }
      setOpen(false);
      setForm({ title: "", description: "", start: "", end: "", color: "#E8B96B", member_ids: [], location: "" });
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/api/events/${id}`);
    toast.success("Removed");
    load();
  };

  // Build month grid
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1).getDay();
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventsForDay = (d) => {
    const dateStr = `${year}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return events.filter((e) => e.start?.startsWith(dateStr));
  };

  // Week view helpers
  const weekStart = (() => {
    const d = new Date(month);
    const dow = d.getDay();
    d.setDate(d.getDate() - dow);
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });
  const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const eventsForDate = (d) => events.filter((e) => e.start?.startsWith(dateKey(d)));
  const HOURS = Array.from({ length: 24 }, (_, i) => i);
  const eventStartHour = (e) => {
    if (!e.start) return 0;
    const t = e.start.includes("T") ? e.start.split("T")[1] : "00:00";
    return parseInt(t.split(":")[0], 10) || 0;
  };
  const eventDurationHours = (e) => {
    if (!e.start || !e.end) return 1;
    try {
      const s = new Date(e.start);
      const en = new Date(e.end);
      const h = (en - s) / 3600000;
      return Math.max(0.75, h);
    } catch { return 1; }
  };

  const navPrev = () => {
    if (view === "month") setMonth(new Date(year, m - 1, 1));
    else if (view === "week") { const d = new Date(month); d.setDate(d.getDate() - 7); setMonth(d); }
    else { const d = new Date(month); d.setDate(d.getDate() - 1); setMonth(d); }
  };
  const navNext = () => {
    if (view === "month") setMonth(new Date(year, m + 1, 1));
    else if (view === "week") { const d = new Date(month); d.setDate(d.getDate() + 7); setMonth(d); }
    else { const d = new Date(month); d.setDate(d.getDate() + 1); setMonth(d); }
  };
  const headerLabel = (() => {
    if (view === "month") return month.toLocaleString("default", { month: "long", year: "numeric" });
    if (view === "week") {
      const end = new Date(weekStart); end.setDate(weekStart.getDate() + 6);
      return `${weekStart.toLocaleString("default", { month: "short", day: "numeric" })} – ${end.toLocaleString("default", { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return month.toLocaleString("default", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  })();

  return (
    <div data-testid="calendar-page">
      <PageHeader
        title="Calendar"
        subtitle="Family events, birthdays, and reminders."
        color="#E9B5A0"
        action={
          <div className="flex gap-2 flex-wrap">
            {googleStatus.connected ? (
              <>
                <NBButton testId="sync-google" color="#B8CFA9" onClick={syncGoogle} disabled={syncing}>
                  <RefreshCw strokeWidth={3} className={`w-4 h-4 inline mr-1 ${syncing ? "animate-spin" : ""}`} />
                  Sync Google
                </NBButton>
                <NBButton testId="disconnect-google" color="white" onClick={disconnectGoogle}>
                  <Unlink strokeWidth={3} className="w-4 h-4 inline mr-1" />
                </NBButton>
              </>
            ) : (
              <NBButton testId="connect-google" color="white" onClick={connectGoogle} disabled={connecting}>
                <Link2 strokeWidth={3} className="w-4 h-4 inline mr-1" />
                {connecting ? "Connecting..." : "Connect Google"}
              </NBButton>
            )}
            <NBButton testId="add-event-btn" color="#E9B5A0" onClick={() => setOpen(true)}>
              <Plus strokeWidth={3} className="w-4 h-4 inline mr-1" />Event
            </NBButton>
          </div>
        }
      />

      {googleStatus.connected && (
        <div className="nb-badge bg-[#B8CFA9] mb-4 inline-flex items-center gap-1.5">
          <Link2 strokeWidth={3} className="w-3 h-3" />
          Synced from {googleStatus.google_email || "Google Calendar"}
        </div>
      )}

      <NBCard className="mb-6" hover={false}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <button onClick={navPrev} className="nb-btn p-2" data-testid="prev-period"><ChevronLeft strokeWidth={2.4} /></button>
          <h2 className="text-2xl font-extrabold text-center flex-1" style={{ fontFamily: "var(--hh-display)" }} data-testid="cal-header-label">{headerLabel}</h2>
          <button onClick={navNext} className="nb-btn p-2" data-testid="next-period"><ChevronRight strokeWidth={2.4} /></button>
          <div className="flex gap-1 ml-2 p-1 rounded-full" style={{ background: "var(--hh-card-soft)", border: "1px solid var(--hh-line)" }} data-testid="view-switcher">
            {["month", "week", "day"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`view-${v}`}
                className="px-3 py-1 text-xs font-semibold rounded-full transition-colors capitalize"
                style={{
                  background: view === v ? "var(--hh-ink)" : "transparent",
                  color: view === v ? "var(--hh-card)" : "var(--hh-ink-soft)",
                }}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {view === "month" && (
          <>
            <div className="grid grid-cols-7 gap-2 text-center text-[10px] uppercase tracking-widest mb-2" style={{ color: "var(--hh-ink-soft)" }}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {cells.map((d, i) => (
                <div key={i} className={`min-h-[88px] p-2 rounded-xl ${d ? "" : "opacity-0"}`}
                     style={d ? { background: "var(--hh-card)", border: "1px solid var(--hh-line)" } : {}}>
                  {d && (
                    <>
                      <p className="text-xs font-semibold" style={{ color: "var(--hh-ink-soft)" }}>{d}</p>
                      <div className="space-y-1 mt-1.5">
                        {eventsForDay(d).slice(0, 2).map((e) => (
                          <div key={e.id} className="text-[11px] px-1.5 py-0.5 rounded font-semibold truncate" style={{ backgroundColor: e.color, color: "var(--hh-ink)" }}>
                            {e.title}
                          </div>
                        ))}
                        {eventsForDay(d).length > 2 && (
                          <div className="text-[10px]" style={{ color: "var(--hh-ink-soft)" }}>+{eventsForDay(d).length - 2} more</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {view === "week" && (
          <div className="overflow-x-auto" data-testid="week-view">
            <div className="grid" style={{ gridTemplateColumns: "60px repeat(7, minmax(120px, 1fr))" }}>
              <div />
              {weekDays.map((d) => {
                const isToday = dateKey(d) === dateKey(new Date());
                return (
                  <div key={d.toISOString()} className="text-center pb-3" style={{ borderBottom: "1px solid var(--hh-line)" }}>
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--hh-ink-soft)" }}>
                      {d.toLocaleString("default", { weekday: "short" })}
                    </div>
                    <div className="text-2xl font-extrabold mt-0.5" style={{ fontFamily: "var(--hh-display)", color: isToday ? "var(--hh-terracotta)" : "var(--hh-ink)" }}>
                      {d.getDate()}
                    </div>
                  </div>
                );
              })}
              {HOURS.map((h) => (
                <React.Fragment key={`w-row-${h}`}>
                  <div className="text-[10px] pr-2 pt-1 text-right" style={{ color: "var(--hh-ink-soft)", borderTop: "1px dashed var(--hh-line)" }}>
                    {h.toString().padStart(2, "0")}:00
                  </div>
                  {weekDays.map((d) => (
                    <div key={`${dateKey(d)}-${h}`} className="relative" style={{ minHeight: 36, borderTop: "1px dashed var(--hh-line)", borderLeft: "1px solid var(--hh-line)" }}>
                      {h === 0 && eventsForDate(d).map((e) => {
                        const top = eventStartHour(e) * 36;
                        const height = eventDurationHours(e) * 36 - 2;
                        return (
                          <div key={e.id}
                               className="absolute left-1 right-1 rounded-md px-2 py-1 text-[11px] font-semibold cursor-pointer overflow-hidden"
                               style={{ top, height, background: e.color, color: "var(--hh-ink)", boxShadow: "0 4px 10px -6px rgba(42,31,23,0.30)" }}
                               data-testid={`week-event-${e.id}`}
                               title={e.title}
                          >
                            <div className="truncate font-bold">{e.title}</div>
                            {e.location && <div className="truncate opacity-80 text-[10px]">{e.location}</div>}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {view === "day" && (
          <div data-testid="day-view">
            <div className="text-center mb-4 text-sm" style={{ color: "var(--hh-ink-soft)" }}>
              {eventsForDate(month).length} event{eventsForDate(month).length === 1 ? "" : "s"} on this day
            </div>
            <div className="grid" style={{ gridTemplateColumns: "60px 1fr" }}>
              {HOURS.map((h) => (
                <React.Fragment key={`d-row-${h}`}>
                  <div className="text-[11px] pr-3 pt-1 text-right" style={{ color: "var(--hh-ink-soft)", borderTop: "1px dashed var(--hh-line)" }}>
                    {h.toString().padStart(2, "0")}:00
                  </div>
                  <div className="relative" style={{ minHeight: 48, borderTop: "1px dashed var(--hh-line)", borderLeft: "1px solid var(--hh-line)" }}>
                    {h === 0 && eventsForDate(month).map((e) => {
                      const top = eventStartHour(e) * 48;
                      const height = eventDurationHours(e) * 48 - 4;
                      return (
                        <div key={e.id}
                             className="absolute left-2 right-2 rounded-lg px-3 py-2 cursor-pointer overflow-hidden"
                             style={{ top, height, background: e.color, color: "var(--hh-ink)", boxShadow: "0 6px 14px -8px rgba(42,31,23,0.30)" }}
                             data-testid={`day-event-${e.id}`}
                        >
                          <div className="font-bold text-sm truncate">{e.title}</div>
                          {e.location && <div className="text-xs opacity-80">{e.location}</div>}
                          {e.description && <div className="text-xs opacity-80 mt-1 line-clamp-2">{e.description}</div>}
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </NBCard>

      <h3 className="text-2xl font-black mb-4" style={{ fontFamily: "var(--hh-display)" }}>All Events</h3>
      {events.length === 0 ? (
        <EmptyState title="No events scheduled" subtitle="Add your first event to see it here." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((e) => (
            <NBCard key={e.id} color={e.color} data-testid={`event-${e.id}`}>
              <div className="flex justify-between items-start gap-2">
                <CalIcon strokeWidth={2.5} className="shrink-0" />
                <div className="flex gap-1 items-center">
                  {e.google_id && <span className="nb-badge bg-white" title="Synced with Google Calendar"><Link2 strokeWidth={3} className="w-3 h-3 inline" /></span>}
                  <button onClick={() => remove(e.id)} className="nb-btn bg-white p-1.5" data-testid={`delete-event-${e.id}`}>
                    <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h4 className="text-lg font-black mt-2" style={{ fontFamily: "var(--hh-display)" }}>{e.title}</h4>
              <p className="text-sm font-bold">{e.start && new Date(e.start).toLocaleString()}</p>
              {e.location && <p className="text-sm mt-1">📍 {e.location}</p>}
              {e.description && <p className="text-sm mt-2">{e.description}</p>}
            </NBCard>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>New Event</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="event-title" required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <NBInput testId="event-start" required type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
              <NBInput testId="event-end" type="datetime-local" placeholder="End (optional)" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
              <NBInput testId="event-location" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              <NBTextarea testId="event-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div>
                <label className="text-xs font-black uppercase tracking-wider mb-2 block">Color</label>
                <div className="flex gap-2">
                  {COLORS.map((c) => (
                    <button type="button" key={c} onClick={() => setForm({ ...form, color: c })}
                      className={`w-10 h-10 rounded-full border-2 border-black ${form.color === c ? "nb-shadow" : ""}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <NBButton testId="submit-event" type="submit" color="#E9B5A0" className="w-full">Save Event</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
